import { useState } from 'react';
import { Upload, Play, Database, AlertCircle, CheckCircle, Loader2, FileText, Download } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface BatchStatus {
  total: number;
  processed: number;
  successful: number;
  failed: number;
  errors: Array<{ username: string; error: string }>;
}

export default function ApifyIntegration() {
  const [instagramUrls, setInstagramUrls] = useState<string[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [status, setStatus] = useState<BatchStatus | null>(null);
  const [logs, setLogs] = useState<string[]>([]);
  const [postsLimit, setPostsLimit] = useState(20);

  const addLog = (message: string) => {
    setLogs(prev => [...prev, `[${new Date().toLocaleTimeString()}] ${message}`]);
  };

  const scrapeCollaborationPosts = async (influencers: any[]) => {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

    let successCount = 0;
    let failCount = 0;

    for (const influencer of influencers) {
      try {
        addLog(`  📸 Récupération des posts de @${influencer.username}...`);

        const response = await fetch(`${supabaseUrl}/functions/v1/scrape-influencer-posts`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': supabaseKey,
            'Authorization': `Bearer ${supabaseKey}`,
          },
          body: JSON.stringify({
            influencerId: influencer.id,
            username: influencer.username,
            resultsLimit: postsLimit,
          }),
        });

        if (response.ok) {
          const postResult = await response.json();
          if (postResult.success) {
            const reelsInfo = postResult.reelsCount > 0 ? ` (${postResult.postsCount} posts + ${postResult.reelsCount} reels)` : '';
            addLog(`  ✅ @${influencer.username}: ${postResult.collaborationPosts} collab(s) sur ${postResult.totalPosts} publications${reelsInfo}`);
            successCount++;
          } else {
            addLog(`  ⚠️ @${influencer.username}: ${postResult.error || 'Erreur inconnue'}`);
            failCount++;
          }
        } else {
          addLog(`  ❌ @${influencer.username}: Erreur HTTP ${response.status}`);
          failCount++;
        }
      } catch (error) {
        addLog(`  ❌ @${influencer.username}: ${error instanceof Error ? error.message : 'Erreur'}`);
        failCount++;
      }
    }

    addLog(`\n📊 Récupération des posts terminée: ${successCount} réussi(s), ${failCount} échoué(s)`);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      const lines = text.split(/[\n,]/).map(line => {
        const trimmed = line.trim();
        if (!trimmed) return '';
        if (trimmed.startsWith('http')) return trimmed;
        return `https://www.instagram.com/${trimmed.replace('@', '')}/`;
      }).filter(line => line);
      setInstagramUrls([...new Set(lines)]);
      addLog(`✓ Fichier chargé: ${lines.length} URLs Instagram détectées`);
    };
    reader.readAsText(file);
  };

  const handleTextInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value;
    const lines = text.split(/[\n,]/).map(line => {
      const trimmed = line.trim();
      if (!trimmed) return '';
      if (trimmed.startsWith('http')) return trimmed;
      return `https://www.instagram.com/${trimmed.replace('@', '')}/`;
    }).filter(line => line);
    setInstagramUrls([...new Set(lines)]);
  };

  const startApifyScraping = async () => {
    if (instagramUrls.length === 0) {
      addLog('❌ Aucune URL Instagram fournie');
      return;
    }

    setIsProcessing(true);
    setStatus({
      total: instagramUrls.length,
      processed: 0,
      successful: 0,
      failed: 0,
      errors: []
    });

    const BATCH_SIZE = 10;
    const batches: string[][] = [];

    for (let i = 0; i < instagramUrls.length; i += BATCH_SIZE) {
      batches.push(instagramUrls.slice(i, i + BATCH_SIZE));
    }

    addLog(`🚀 Démarrage de la récupération de ${instagramUrls.length} profils Instagram en ${batches.length} lot(s) de ${BATCH_SIZE}...`);

    let totalSuccessful = 0;
    let totalFailed = 0;
    const allErrors: Array<{ username: string; error: string }> = [];
    const allInfluencers: any[] = [];

    try {
      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/instagram-scraper`;

      for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
        const batch = batches[batchIndex];
        const batchNumber = batchIndex + 1;

        addLog(`\n📦 Traitement du lot ${batchNumber}/${batches.length} (${batch.length} profils)...`);
        addLog(`⏳ Cela peut prendre quelques minutes, veuillez patienter...`);

        try {
          const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
              'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
            },
            body: JSON.stringify({
              instagramUrls: batch,
              resultsType: 'posts',
              resultsLimit: 200
            })
          });

          if (!response.ok) {
            const errorText = await response.text();
            let errorData;
            try {
              errorData = JSON.parse(errorText);
            } catch {
              errorData = { error: errorText };
            }
            throw new Error(errorData.error || `Erreur HTTP ${response.status}: ${response.statusText}`);
          }

          const result = await response.json();

          if (result.success) {
            addLog(`✅ Lot ${batchNumber} terminé avec succès!`);
            addLog(`📊 Run ID Apify: ${result.runId}`);
            addLog(`📥 ${result.totalResults} résultats récupérés`);
            addLog(`✨ ${result.influencersInserted} influenceurs ajoutés à la base`);

            totalSuccessful += result.influencersInserted;

            if (result.errors && result.errors.length > 0) {
              addLog(`⚠️ ${result.errors.length} erreur(s) dans ce lot`);
              allErrors.push(...result.errors);
              totalFailed += result.errors.length;
            }

            if (result.influencers && result.influencers.length > 0) {
              allInfluencers.push(...result.influencers);
            }
          } else {
            throw new Error(result.error || 'Erreur inconnue');
          }

          setStatus(prev => ({
            total: instagramUrls.length,
            processed: (batchIndex + 1) * BATCH_SIZE,
            successful: totalSuccessful,
            failed: totalFailed,
            errors: allErrors
          }));

          if (batchIndex < batches.length - 1) {
            addLog(`⏸️ Pause de 2 secondes avant le prochain lot...`);
            await new Promise(resolve => setTimeout(resolve, 2000));
          }

        } catch (batchError) {
          addLog(`❌ Erreur lot ${batchNumber}: ${batchError instanceof Error ? batchError.message : 'Erreur inconnue'}`);
          batch.forEach(url => {
            const username = url.split('/').filter(Boolean).pop() || 'unknown';
            allErrors.push({ username, error: 'Erreur de traitement du lot' });
          });
          totalFailed += batch.length;
        }
      }

      if (allInfluencers.length > 0) {
        addLog(`\n🔍 Démarrage de la récupération des posts de collaboration pour ${allInfluencers.length} influenceur(s)...`);
        await scrapeCollaborationPosts(allInfluencers);
      }

      addLog(`\n✅ Processus terminé!`);
      addLog(`📊 Total: ${totalSuccessful} succès, ${totalFailed} échec(s)`);

      if (allErrors.length > 0) {
        addLog(`\n⚠️ Détail des erreurs:`);
        allErrors.forEach((err: any) => {
          addLog(`  ❌ ${err.username || 'Unknown'}: ${err.error}`);
        });
      }

      setStatus({
        total: instagramUrls.length,
        processed: instagramUrls.length,
        successful: totalSuccessful,
        failed: totalFailed,
        errors: allErrors
      });

    } catch (error) {
      addLog(`❌ Erreur générale: ${error instanceof Error ? error.message : 'Erreur inconnue'}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const checkDatabaseCount = async () => {
    addLog('🔍 Vérification du nombre d\'influenceurs dans la base...');
    try {
      const { count, error } = await supabase
        .from('influencers')
        .select('*', { count: 'exact', head: true });

      if (error) throw error;
      addLog(`📊 Total influenceurs en base: ${count || 0}`);
    } catch (error) {
      addLog(`❌ Erreur: ${error instanceof Error ? error.message : 'Erreur inconnue'}`);
    }
  };

  const downloadTemplate = () => {
    const template = `username1
username2
username3
# ou séparés par des virgules: username1, username2, username3`;
    const blob = new Blob([template], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'instagram_usernames_template.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Enrichissement Apify</h1>
        <p className="text-gray-600">Importez vos pseudos Instagram et enrichissez automatiquement votre base de données</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Configuration Apify</h2>
              <Database className="h-5 w-5 text-blue-600" />
            </div>

            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-green-900">
                    <p className="font-medium mb-1">Configuration automatique</p>
                    <p className="text-green-700">Votre token Apify est déjà configuré de manière sécurisée</p>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex gap-3">
                  <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-900">
                    <p className="font-medium mb-1">Traitement automatique</p>
                    <p className="text-blue-700">Les données sont scrapées et ajoutées automatiquement à Supabase</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Import des pseudos</h2>
              <FileText className="h-5 w-5 text-blue-600" />
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fichier texte (.txt, .csv)
                </label>
                <div className="flex gap-2">
                  <label className="flex-1 flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors">
                    <Upload className="h-5 w-5 text-gray-400" />
                    <span className="text-sm text-gray-600">Choisir un fichier</span>
                    <input
                      type="file"
                      accept=".txt,.csv"
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                  </label>
                  <button
                    onClick={downloadTemplate}
                    className="px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    title="Télécharger un template"
                  >
                    <Download className="h-5 w-5 text-gray-600" />
                  </button>
                </div>
              </div>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">ou</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre de posts à scraper par profil
                </label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={postsLimit}
                  onChange={(e) => setPostsLimit(Math.min(100, Math.max(1, parseInt(e.target.value) || 20)))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Recommandé: 20-30 posts pour optimiser les coûts Apify
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Coller les URLs ou pseudos Instagram (un par ligne ou séparés par des virgules)
                </label>
                <textarea
                  rows={6}
                  onChange={handleTextInput}
                  placeholder="https://www.instagram.com/username1/&#10;username2&#10;https://www.instagram.com/username3/"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                />
              </div>

              {instagramUrls.length > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="text-sm font-medium text-green-900">
                      {instagramUrls.length} URLs Instagram prêtes à être scrapées
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={startApifyScraping}
              disabled={isProcessing || instagramUrls.length === 0}
              className="flex-1 flex items-center justify-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span>Traitement en cours...</span>
                </>
              ) : (
                <>
                  <Play className="h-5 w-5" />
                  <span>Lancer l'enrichissement</span>
                </>
              )}
            </button>

            <button
              onClick={checkDatabaseCount}
              className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              title="Vérifier la base de données"
            >
              <Database className="h-5 w-5 text-gray-600" />
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Logs en temps réel</h2>
          <div className="bg-gray-900 rounded-lg p-4 h-[600px] overflow-y-auto font-mono text-sm">
            {logs.length === 0 ? (
              <p className="text-gray-500">En attente de démarrage...</p>
            ) : (
              <div className="space-y-1">
                {logs.map((log, index) => (
                  <div key={index} className="text-gray-300">
                    {log}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex gap-3">
          <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-yellow-900">
            <p className="font-medium mb-1">Notes importantes</p>
            <ul className="list-disc list-inside space-y-1 text-yellow-800">
              <li>Le traitement peut prendre 2-5 minutes selon le nombre de profils</li>
              <li>Les données sont automatiquement ajoutées à votre base Supabase</li>
              <li>Les doublons sont gérés automatiquement (par platform + username)</li>
              <li>Vous pouvez scraper jusqu'à 200 posts par profil</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
